export const COMPANION_MESSAGE_MAX_CODE_POINTS = 32_768;
export const COMPANION_REQUEST_ID_MAX_LENGTH = 128;
export const COMPANION_AGENT_NAME_MAX_LENGTH = 256;
export const COMPANION_DESKTOP_AFTER_MAX_LENGTH = 256;
export const COMPANION_BRIDGE_LINE_MAX_LENGTH = 140_000;
export const COMPANION_HISTORY_LIMIT_MAX = 40;
export const COMPANION_TURN_TEXT_MAX_CODE_POINTS = 32_768;
export const COMPANION_REPLY_DELTA_MAX_CODE_POINTS = 4_096;
export const COMPANION_TURN_ID_MAX_LENGTH = 256;

export type PersonaKey = "startup-advisor" | "ezra";

/** One rendered message of a mate's canonical conversation. */
export interface CompanionTurn {
  readonly id: string;
  readonly role: "user" | "mate";
  readonly text: string;
  readonly at: string | null;
}

export interface CompanionMate {
  readonly personaKey: PersonaKey;
  readonly agentName: string;
  readonly displayName: "Stacey" | "Ezra";
  readonly portraitKey: "stacey" | "ezra";
  readonly activeSince: string;
}

export type CompanionBridgeRequest =
  | { readonly type: "roster" }
  | { readonly type: "update" }
  | {
      readonly type: "send";
      readonly requestId: string;
      readonly personaKey: PersonaKey;
      readonly draftRevision: number;
      readonly text: string;
    }
  /**
   * `send`, plus the mate's reply streamed back as reply-delta events. A
   * separate type — never a flag on `send` — so a helper that predates it
   * rejects the whole request cleanly instead of half-understanding it, and an
   * app that predates it can never receive events it cannot parse.
   */
  | {
      readonly type: "converse";
      readonly requestId: string;
      readonly personaKey: PersonaKey;
      readonly draftRevision: number;
      readonly text: string;
    }
  | {
      readonly type: "history";
      readonly requestId: string;
      readonly personaKey: PersonaKey;
      readonly limit: number;
    };

export type CompanionBridgeEvent =
  /**
   * What the release channel offers against what is installed. The app has no
   * way to reach the channel itself — no network of any kind — so this is the
   * only way it can know it is out of date.
   */
  | {
      readonly type: "update";
      readonly installed: string | null;
      readonly available: string | null;
      readonly severity: "critical" | "recommended" | "optional" | null;
    }
  | {
      readonly type: "roster";
      readonly origin: string;
      readonly mates: readonly CompanionMate[];
    }
  | {
      readonly type: "dispatching";
      readonly requestId: string;
      readonly personaKey: PersonaKey;
      readonly route: string;
    }
  | {
      readonly type: "accepted";
      readonly requestId: string;
      readonly personaKey: PersonaKey;
      readonly route: string;
    }
  | {
      readonly type: "completed";
      readonly requestId: string;
    }
  | {
      readonly type: "turn";
      readonly requestId: string;
      readonly turn: CompanionTurn;
    }
  | {
      readonly type: "history-end";
      readonly requestId: string;
      readonly count: number;
    }
  | {
      readonly type: "reply-delta";
      readonly requestId: string;
      readonly text: string;
    }
  | {
      readonly type: "indeterminate";
      readonly requestId: string;
      readonly stage: "dispatch" | "after-accept";
      readonly route: string;
    }
  | {
      readonly type: "failed";
      readonly requestId?: string;
      readonly reason:
        | "not-connected"
        | "not-authorized"
        | "mate-unavailable"
        | "busy"
        | "bad-request";
    };

const PERSONAS = new Set<PersonaKey>([
  "startup-advisor",
  "ezra",
]);
const FAILURE_REASONS = new Set([
  "not-connected",
  "not-authorized",
  "mate-unavailable",
  "busy",
  "bad-request",
]);
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/u;
const URL_SAFE_OPAQUE_ID = /^[A-Za-z0-9_-]+$/u;
const SEVERITIES = new Set(["critical", "recommended", "optional"]);
/** Bounded and printable: a version reaches a window, and nothing else does. */
const VERSION = /^[A-Za-z0-9][A-Za-z0-9._+-]{0,63}$/u;

function isVersionOrNull(value: unknown): boolean {
  return value === null || (typeof value === "string" && VERSION.test(value));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" && value !== null && !Array.isArray(value)
  );
}

function hasExactKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
): boolean {
  const actual = Object.keys(value);
  return (
    actual.length === keys.length &&
    keys.every((key) => Object.hasOwn(value, key))
  );
}

function isPersonaKey(value: unknown): value is PersonaKey {
  return typeof value === "string" && PERSONAS.has(value as PersonaKey);
}

function isBoundedPlainString(
  value: unknown,
  maxLength: number,
): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= maxLength &&
    !CONTROL_CHARACTERS.test(value)
  );
}

function isRequestId(value: unknown): value is string {
  return isBoundedPlainString(value, COMPANION_REQUEST_ID_MAX_LENGTH);
}

/**
 * Message text may span lines and be indented; every other control character
 * is refused. Distinct from the single-line rule that guards labels and ids.
 */
const FORBIDDEN_TEXT_CHARACTERS =
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/u;

function isBoundedMessageText(
  value: unknown,
  maxCodePoints: number,
): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    !FORBIDDEN_TEXT_CHARACTERS.test(value) &&
    [...value].length <= maxCodePoints
  );
}

/**
 * A parseable ISO-8601 UTC instant, or null. Looser than the roster's rule on
 * purpose: a message stamped moments ago on a skewed server clock must not
 * invalidate a whole transcript.
 */
const INSTANT_SHAPE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;

function isInstantOrNull(value: unknown): boolean {
  return (
    value === null ||
    (typeof value === "string" &&
      INSTANT_SHAPE.test(value) &&
      Number.isFinite(Date.parse(value)))
  );
}

function isCompanionTurn(value: unknown): value is CompanionTurn {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["id", "role", "text", "at"]) &&
    isBoundedPlainString(value.id, COMPANION_TURN_ID_MAX_LENGTH) &&
    (value.role === "user" || value.role === "mate") &&
    isBoundedMessageText(value.text, COMPANION_TURN_TEXT_MAX_CODE_POINTS) &&
    isInstantOrNull(value.at)
  );
}

function isCanonicalInstant(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const time = Date.parse(value);
  return (
    Number.isFinite(time) &&
    new Date(time).toISOString() === value &&
    time <= Date.now()
  );
}

function requestError(): never {
  throw new Error("Invalid companion bridge request");
}

function eventError(): never {
  throw new Error("Invalid companion bridge event");
}

export function isGatewayOrigin(value: unknown): value is string {
  if (typeof value !== "string" || !value.startsWith("https://")) {
    return false;
  }
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }
  return (
    parsed.origin === value &&
    parsed.protocol === "https:" &&
    parsed.username === "" &&
    parsed.password === "" &&
    parsed.pathname === "/" &&
    parsed.search === "" &&
    parsed.hash === ""
  );
}

export function isValidMateRoute(value: unknown): value is string {
  if (
    typeof value !== "string" ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("#")
  ) {
    return false;
  }

  try {
    const parsed = new URL(value, "https://companion.invalid");
    if (
      parsed.origin !== "https://companion.invalid" ||
      !/^\/mates\/(?:startup-advisor|ezra)$/u.test(parsed.pathname)
    ) {
      return false;
    }

    const entries = [...parsed.searchParams.entries()];
    const keys = entries.map(([key]) => key);
    if (
      new Set(keys).size !== keys.length ||
      keys.some(
        (key) => key !== "desktopPending" && key !== "desktopAfter",
      )
    ) {
      return false;
    }

    if (
      parsed.searchParams.has("desktopPending") &&
      parsed.searchParams.get("desktopPending") !== "1"
    ) {
      return false;
    }

    if (parsed.searchParams.has("desktopAfter")) {
      const after = parsed.searchParams.get("desktopAfter");
      if (
        after === null ||
        after.length === 0 ||
        after.length > COMPANION_DESKTOP_AFTER_MAX_LENGTH ||
        !URL_SAFE_OPAQUE_ID.test(after)
      ) {
        return false;
      }
    }

    return true;
  } catch {
    return false;
  }
}

export function parseRequest(value: unknown): CompanionBridgeRequest {
  if (!isRecord(value)) return requestError();

  if (value.type === "roster") {
    if (!hasExactKeys(value, ["type"])) return requestError();
    return { type: "roster" };
  }

  if (value.type === "update") {
    if (!hasExactKeys(value, ["type"])) return requestError();
    return { type: "update" };
  }

  if (value.type === "history") {
    if (
      !hasExactKeys(value, ["type", "requestId", "personaKey", "limit"]) ||
      !isRequestId(value.requestId) ||
      !isPersonaKey(value.personaKey) ||
      !Number.isSafeInteger(value.limit) ||
      (value.limit as number) < 1 ||
      (value.limit as number) > COMPANION_HISTORY_LIMIT_MAX
    ) {
      return requestError();
    }
    return {
      type: "history",
      requestId: value.requestId,
      personaKey: value.personaKey,
      limit: value.limit as number,
    };
  }

  if (
    (value.type !== "send" && value.type !== "converse") ||
    !hasExactKeys(value, [
      "type",
      "requestId",
      "personaKey",
      "draftRevision",
      "text",
    ]) ||
    !isRequestId(value.requestId) ||
    !isPersonaKey(value.personaKey) ||
    !Number.isSafeInteger(value.draftRevision) ||
    (value.draftRevision as number) < 0 ||
    typeof value.text !== "string" ||
    value.text.trim().length === 0 ||
    [...value.text].length > COMPANION_MESSAGE_MAX_CODE_POINTS
  ) {
    return requestError();
  }

  return {
    type: value.type,
    requestId: value.requestId,
    personaKey: value.personaKey,
    draftRevision: value.draftRevision as number,
    text: value.text,
  };
}

export function serializeRequest(value: CompanionBridgeRequest): string {
  return JSON.stringify(parseRequest(value));
}

export function parseRequestLine(line: string): CompanionBridgeRequest {
  if (
    typeof line !== "string" ||
    line.length > COMPANION_BRIDGE_LINE_MAX_LENGTH ||
    /[\r\n]/u.test(line)
  ) {
    return requestError();
  }

  try {
    return parseRequest(JSON.parse(line));
  } catch {
    return requestError();
  }
}

function parseMate(value: unknown): CompanionMate {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "personaKey",
      "agentName",
      "displayName",
      "portraitKey",
      "activeSince",
    ]) ||
    !isPersonaKey(value.personaKey) ||
    !isBoundedPlainString(
      value.agentName,
      COMPANION_AGENT_NAME_MAX_LENGTH,
    ) ||
    !isCanonicalInstant(value.activeSince)
  ) {
    return eventError();
  }

  const identity =
    value.personaKey === "startup-advisor"
      ? { displayName: "Stacey" as const, portraitKey: "stacey" as const }
      : { displayName: "Ezra" as const, portraitKey: "ezra" as const };
  if (
    value.displayName !== identity.displayName ||
    value.portraitKey !== identity.portraitKey
  ) {
    return eventError();
  }

  return {
    personaKey: value.personaKey,
    agentName: value.agentName,
    displayName: identity.displayName,
    portraitKey: identity.portraitKey,
    activeSince: value.activeSince,
  };
}

export function parseEvent(value: unknown): CompanionBridgeEvent {
  if (!isRecord(value)) return eventError();

  if (value.type === "update") {
    if (
      !hasExactKeys(value, ["type", "installed", "available", "severity"]) ||
      !isVersionOrNull(value.installed) ||
      !isVersionOrNull(value.available) ||
      !(value.severity === null || SEVERITIES.has(value.severity as string))
    ) {
      return eventError();
    }
    return {
      type: "update",
      installed: value.installed as string | null,
      available: value.available as string | null,
      severity: value.severity as "critical" | "recommended" | "optional" | null,
    };
  }

  if (value.type === "roster") {
    if (
      !hasExactKeys(value, ["type", "origin", "mates"]) ||
      !isGatewayOrigin(value.origin) ||
      !Array.isArray(value.mates) ||
      value.mates.length !== 2
    ) {
      return eventError();
    }
    const mates = value.mates.map(parseMate);
    if (
      new Set(mates.map(({ personaKey }) => personaKey)).size !== 2 ||
      new Set(mates.map(({ agentName }) => agentName)).size !== 2
    ) {
      return eventError();
    }
    return { type: "roster", origin: value.origin, mates };
  }

  if (value.type === "failed") {
    const keys =
      value.requestId === undefined
        ? ["type", "reason"]
        : ["type", "requestId", "reason"];
    if (
      !hasExactKeys(value, keys) ||
      (value.requestId !== undefined && !isRequestId(value.requestId)) ||
      typeof value.reason !== "string" ||
      !FAILURE_REASONS.has(value.reason)
    ) {
      return eventError();
    }
    return value.requestId === undefined
      ? {
          type: "failed",
          reason: value.reason as Extract<
            CompanionBridgeEvent,
            { type: "failed" }
          >["reason"],
        }
      : {
          type: "failed",
          requestId: value.requestId,
          reason: value.reason as Extract<
            CompanionBridgeEvent,
            { type: "failed" }
          >["reason"],
        };
  }

  if (value.type === "completed") {
    if (
      !hasExactKeys(value, ["type", "requestId"]) ||
      !isRequestId(value.requestId)
    ) {
      return eventError();
    }
    return { type: "completed", requestId: value.requestId };
  }

  if (value.type === "turn") {
    if (
      !hasExactKeys(value, ["type", "requestId", "turn"]) ||
      !isRequestId(value.requestId) ||
      !isCompanionTurn(value.turn)
    ) {
      return eventError();
    }
    return {
      type: "turn",
      requestId: value.requestId,
      turn: {
        id: value.turn.id,
        role: value.turn.role,
        text: value.turn.text,
        at: value.turn.at,
      },
    };
  }

  if (value.type === "history-end") {
    if (
      !hasExactKeys(value, ["type", "requestId", "count"]) ||
      !isRequestId(value.requestId) ||
      !Number.isSafeInteger(value.count) ||
      (value.count as number) < 0 ||
      (value.count as number) > COMPANION_HISTORY_LIMIT_MAX
    ) {
      return eventError();
    }
    return {
      type: "history-end",
      requestId: value.requestId,
      count: value.count as number,
    };
  }

  if (value.type === "reply-delta") {
    if (
      !hasExactKeys(value, ["type", "requestId", "text"]) ||
      !isRequestId(value.requestId) ||
      !isBoundedMessageText(value.text, COMPANION_REPLY_DELTA_MAX_CODE_POINTS)
    ) {
      return eventError();
    }
    return {
      type: "reply-delta",
      requestId: value.requestId,
      text: value.text,
    };
  }

  if (value.type === "dispatching" || value.type === "accepted") {
    if (
      !hasExactKeys(value, [
        "type",
        "requestId",
        "personaKey",
        "route",
      ]) ||
      !isRequestId(value.requestId) ||
      !isPersonaKey(value.personaKey) ||
      !isValidMateRoute(value.route) ||
      !value.route.startsWith(`/mates/${value.personaKey}`)
    ) {
      return eventError();
    }
    return {
      type: value.type,
      requestId: value.requestId,
      personaKey: value.personaKey,
      route: value.route,
    };
  }

  if (value.type === "indeterminate") {
    if (
      !hasExactKeys(value, ["type", "requestId", "stage", "route"]) ||
      !isRequestId(value.requestId) ||
      (value.stage !== "dispatch" && value.stage !== "after-accept") ||
      !isValidMateRoute(value.route)
    ) {
      return eventError();
    }
    return {
      type: "indeterminate",
      requestId: value.requestId,
      stage: value.stage,
      route: value.route,
    };
  }

  return eventError();
}

export function serializeEvent(value: CompanionBridgeEvent): string {
  return JSON.stringify(parseEvent(value));
}

export function parseEventLine(line: string): CompanionBridgeEvent {
  if (
    typeof line !== "string" ||
    line.length > COMPANION_BRIDGE_LINE_MAX_LENGTH ||
    /[\r\n]/u.test(line)
  ) {
    return eventError();
  }

  try {
    return parseEvent(JSON.parse(line));
  } catch {
    return eventError();
  }
}

export function validateEventSequence(
  events: readonly CompanionBridgeEvent[],
): boolean {
  let parsed: CompanionBridgeEvent[];
  try {
    parsed = events.map(parseEvent);
  } catch {
    return false;
  }
  if (parsed.length === 0) return false;

  const first = parsed[0];
  if (first.type === "roster" || first.type === "failed") {
    return parsed.length === 1;
  }

  if (first.type === "turn" || first.type === "history-end") {
    const last = parsed.at(-1);
    if (last?.type !== "history-end") return false;
    const turns = parsed.slice(0, -1);
    return (
      last.count === turns.length &&
      turns.every(
        (event) =>
          event.type === "turn" && event.requestId === last.requestId,
      )
    );
  }

  if (first.type !== "dispatching") return false;

  if (parsed.length === 2 && parsed[1]?.type === "indeterminate") {
    const terminal = parsed[1];
    return (
      terminal.requestId === first.requestId &&
      terminal.stage === "dispatch" &&
      terminal.route === first.route
    );
  }

  if (parsed.length < 3 || parsed[1]?.type !== "accepted") return false;
  const accepted = parsed[1];
  if (
    accepted.requestId !== first.requestId ||
    accepted.personaKey !== first.personaKey ||
    accepted.route !== first.route
  ) {
    return false;
  }

  const deltas = parsed.slice(2, -1);
  if (
    !deltas.every(
      (event) =>
        event.type === "reply-delta" &&
        event.requestId === first.requestId,
    )
  ) {
    return false;
  }

  const terminal = parsed.at(-1);
  if (
    terminal?.type === "completed" &&
    terminal.requestId === first.requestId
  ) {
    return true;
  }
  return (
    terminal?.type === "indeterminate" &&
    terminal.requestId === first.requestId &&
    terminal.stage === "after-accept" &&
    terminal.route === first.route
  );
}
